import React, { useMemo, useState } from 'react'
import { Table as EdsTable, Pagination, Icon, Button, CircularProgress, Typography } from '@equinor/eds-core-react'
import { ColumnDef, getCoreRowModel, useReactTable, flexRender } from '@tanstack/react-table'
import styled from 'styled-components'
import { Sensor } from '@/features/dashboard/models'
import { useGetDevices } from '@/core/hooks-and-services/devicesService'
import {
    assignment_important,
    close_circle_outlined,
    edit,
    arrow_down,
    arrow_up,
    check_circle_outlined,
    delete_to_trash,
    autorenew,
    sync_problem,
} from '@equinor/eds-icons'
import { TableFooter, TableWrapper } from '@/styles'
import { InstalledSensorStatus, SortItem } from '@/core/models/types'
import { STATUS_COLORS } from '@/core/models/sensorStatusColors'
import useSortSensors from '../sensorCards/useSortSensors'
import { SORT_OPTIONS } from './TableBar'
import { useAtom } from 'jotai'
import {
    pageIndexDashboardAtom,
    selectedSensorCardAtom,
    tableHeaderSortTagAtom,
    tableHeaderSortDeviceAtom,
} from '@/core/store/globalStore'
import { TimeseriesDto } from '@/core/models/types'

interface Props {
    filteredSensors: Sensor[]
    onRefetchSensors: () => void
    sortSpecification: {
        tag: SortItem
        installationDate: SortItem
        device: SortItem
        sensorStatus: SortItem
        batteryCapacity: SortItem
        signalStrength: SortItem
    }
    onEditSensor: (sensor: Sensor) => void
    totalItems?: number
    serverPaginated?: boolean
    pageSize?: number
}

const Table = React.memo((props: Props) => {
    const {
        filteredSensors,
        sortSpecification = {
            tag: { ...SORT_OPTIONS.tag, selected: null },
            installationDate: SORT_OPTIONS.installationDate,
            device: { ...SORT_OPTIONS.device, selected: null },
            sensorStatus: { ...SORT_OPTIONS.sensorStatus, selected: null },
            batteryCapacity: { ...SORT_OPTIONS.batteryCapacity, selected: null },
            signalStrength: { ...SORT_OPTIONS.signalStrength, selected: null },
        },
        onEditSensor,
        totalItems,
        serverPaginated = false,
        pageSize,
    } = props

    const [, setSelectedSensorCard] = useAtom(selectedSensorCardAtom)
    const [timeSeries, setTimeSeries] = useState<TimeseriesDto | null>(null)

    const [tableHeaderSortTag, setTableHeaderSortTag] = useAtom(tableHeaderSortTagAtom)
    const [tableHeaderSortDevice, setTableHeaderSortDevice] = useAtom(tableHeaderSortDeviceAtom)

    const [isTableSortTagActive, setTableSortTagActive] = useState<boolean>(false)
    const [isTableSortDeviceActive, setTableSortDeviceActive] = useState<boolean>(false)

    const { data: devices } = useGetDevices()
    const filteredDevices = devices.filter((device) => device.portfolio)

    const handleDetails = (sensor: Sensor) => {
        setSelectedSensorCard({ sensor: sensor, editOnboarded: false, show: true })
    }

    const handleEdit = (sensor: Sensor) => {
        const isOnboarded =
            sensor.status === InstalledSensorStatus.Onboarded ||
            sensor.status === InstalledSensorStatus.Active ||
            sensor.status === InstalledSensorStatus.Offline ||
            sensor.status === InstalledSensorStatus.Prepared

        if (isOnboarded) {
            setSelectedSensorCard({ sensor: sensor, editOnboarded: true, show: true })
        } else {
            setSelectedSensorCard({ sensor: sensor, editOnboarded: true, show: false })
            onEditSensor(sensor)
        }
    }

    const toggleSortOnTag = () => {
        setTableHeaderSortTag((prev) => (prev === 'Z-A' ? 'A-Z' : 'Z-A'))
        setTableSortTagActive(true)
        setTableSortDeviceActive(false)
    }

    const toggleSortOnSensorModel = () => {
        setTableHeaderSortDevice((prev) => (prev === 'Z-A' ? 'A-Z' : 'Z-A'))
        setTableSortDeviceActive(true)
        setTableSortTagActive(false)
    }

    const columns: ColumnDef<Sensor, any>[] = useMemo(
        () => [
            {
                header: () => (
                    <StyledHeaderSortButton onClick={toggleSortOnTag} variant="ghost">
                        <Typography group="table" variant="cell_header" color="secondary">
                            Tag
                        </Typography>
                        {isTableSortTagActive ? (
                            <Icon color="secondary" data={tableHeaderSortTag === 'A-Z' ? arrow_down : arrow_up} />
                        ) : null}
                    </StyledHeaderSortButton>
                ),
                accessorKey: 'tag',
            },
            {
                header: 'Plant',
                accessorKey: 'plant',
            },
            {
                header: 'Superior Tag',
                accessorKey: 'superiorTag',
            },
            {
                header: () => (
                    <StyledHeaderSortButton onClick={toggleSortOnSensorModel} variant="ghost">
                        <Typography group="table" variant="cell_header" color="secondary">
                            Device
                        </Typography>
                        {isTableSortDeviceActive ? (
                            <Icon color="secondary" data={tableHeaderSortDevice === 'A-Z' ? arrow_down : arrow_up} />
                        ) : null}
                    </StyledHeaderSortButton>
                ),
                accessorKey: 'sensorModel',
                cell: ({ row }) => {
                    const device = filteredDevices.find((d) => d.key === row.original.sensorModel)
                    return device ? device.displayName : '-'
                },
            },
            {
                header: 'Status',
                accessorKey: 'status',
                cell: ({ row }) => {
                    const status = row.original.status
                    const iconConfig = (() => {
                        switch (status) {
                            case InstalledSensorStatus.Draft:
                                return { icon: assignment_important, color: STATUS_COLORS[InstalledSensorStatus.Draft] }
                            case InstalledSensorStatus.Prepared:
                                return {
                                    icon: check_circle_outlined,
                                    color: STATUS_COLORS[InstalledSensorStatus.Prepared],
                                }
                            case InstalledSensorStatus.Offline:
                                return {
                                    icon: close_circle_outlined,
                                    color: STATUS_COLORS[InstalledSensorStatus.Offline],
                                }
                            case InstalledSensorStatus.Unsuccessful:
                                return {
                                    icon: sync_problem,
                                    color: STATUS_COLORS[InstalledSensorStatus.Unsuccessful],
                                }
                            case InstalledSensorStatus.Deleted:
                                return { icon: delete_to_trash, color: STATUS_COLORS[InstalledSensorStatus.Deleted] }
                            case InstalledSensorStatus.Onboarded:
                            case InstalledSensorStatus.Active:
                                return {
                                    icon: check_circle_outlined,
                                    color: STATUS_COLORS[InstalledSensorStatus.Active],
                                }
                            case InstalledSensorStatus.Start:
                                return { icon: autorenew, color: STATUS_COLORS[InstalledSensorStatus.Start] }
                            default:
                                return null
                        }
                    })()

                    return (
                        <StatusContainer>
                            {iconConfig && <Icon data={iconConfig.icon} style={{ color: iconConfig.color }} />}
                            <SensorStatus>{status ?? '-'}</SensorStatus>
                        </StatusContainer>
                    )
                },
            },
            {
                accessorKey: 'battery',
                header: 'Battery',
                cell: () => (
                    <BatteryStatusContainer>
                        <BatteryIcon>
                            <Icon name="battery"></Icon>
                        </BatteryIcon>
                        <BatteryStatusValue>
                            {timeSeries?.battery ? timeSeries?.battery.value + ' %' : '-'}
                        </BatteryStatusValue>
                    </BatteryStatusContainer>
                ),
            },
            {
                accessorKey: 'signal',
                header: 'Signal',
                cell: () => (
                    <SignalStatusContainer>
                        <Icon name="wifi"></Icon>
                        <SignalStatusValue>{timeSeries?.signalQuality ?? '-'}</SignalStatusValue>
                    </SignalStatusContainer>
                ),
            },
            {
                id: 'details',
                header: 'Details',
                cell: ({ row }) => (
                    <Button variant="ghost" onClick={() => handleDetails(row.original)} k>
                        <Icon name="go_to"></Icon>
                    </Button>
                ),
            },
            {
                id: '_actions',
                header: '',
                cell: (ctx) => {
                    const sensorId = ctx.row.original.sensorDbKey
                    const isDeleting = ctx.row.original.lastRequestedOperation === 'Delete'

                    return (
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {(ctx.row.original.status === InstalledSensorStatus.Draft ||
                                ctx.row.original.status === InstalledSensorStatus.Unsuccessful ||
                                ctx.row.original.status === InstalledSensorStatus.Onboarded ||
                                ctx.row.original.status === InstalledSensorStatus.Active ||
                                ctx.row.original.status === InstalledSensorStatus.Offline ||
                                ctx.row.original.status === InstalledSensorStatus.Prepared) && (
                                <Button
                                    aria-label="Modify"
                                    variant="ghost_icon"
                                    color="secondary"
                                    onClick={() => {
                                        if (sensorId) handleEdit(ctx.row.original)
                                    }}
                                    disabled={isDeleting || !sensorId}
                                >
                                    {isDeleting ? (
                                        <CircularProgress value={0} size={24} aria-label="Modify" />
                                    ) : (
                                        <Icon data={edit} />
                                    )}
                                </Button>
                            )}
                        </div>
                    )
                },
                size: 30,
            },
        ],
        [tableHeaderSortTag, tableHeaderSortDevice]
    )

    const itemsPerPage = pageSize ?? 25
    const [currentPageIndex, setCurrentPageIndex] = useAtom(pageIndexDashboardAtom)

    const sortedSensors = useSortSensors({
        filteredSensors: serverPaginated ? [] : filteredSensors,
        sortSpecification,
    })

    const paginatedData = useMemo(() => {
        if (serverPaginated) return filteredSensors
        const startIndex = (currentPageIndex - 1) * itemsPerPage
        return sortedSensors.slice(startIndex, startIndex + itemsPerPage)
    }, [sortedSensors, currentPageIndex, itemsPerPage, serverPaginated, filteredSensors])

    const table = useReactTable({
        data: paginatedData,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row, index) => row.sensorDbKey ?? `row-${index}`,
    })

    const tableRows =
        table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => (
                <EdsTable.Row key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                        <EdsTable.Cell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </EdsTable.Cell>
                    ))}
                </EdsTable.Row>
            ))
        ) : (
            <EdsTable.Row key={0}>
                <EdsTable.Cell colSpan={table.getFlatHeaders().length}>There are no data available.</EdsTable.Cell>
            </EdsTable.Row>
        )

    return (
        <TableWrapper>
            <EdsTableStyled>
                <EdsTable.Head>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <EdsTable.Row key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <EdsTable.Cell key={header.id}>
                                    {header.isPlaceholder
                                        ? null
                                        : flexRender(header.column.columnDef.header, header.getContext())}
                                </EdsTable.Cell>
                            ))}
                        </EdsTable.Row>
                    ))}
                </EdsTable.Head>
                <EdsTable.Body>{tableRows}</EdsTable.Body>
            </EdsTableStyled>
            <TableFooter>
                <Pagination
                    totalItems={totalItems ?? filteredSensors.length}
                    itemsPerPage={itemsPerPage}
                    defaultPage={currentPageIndex}
                    onChange={(e, page) => setCurrentPageIndex(page)}
                    withItemIndicator
                    key={currentPageIndex}
                />
            </TableFooter>
        </TableWrapper>
    )
})

const BatteryStatusContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    margin-left: -2px;
`

const BatteryIcon = styled.div`
    transform: rotate(90deg);
`

const SignalStatusContainer = styled.div`
    gap: 8px;
    display: flex;
    flex-direction: row;
    align-items: center;
`

const StyledHeaderSortButton = styled(Button)`
    margin-left: -17px;
`
const BatteryStatusValue = styled(Typography)`
    margin-left: 5px;
    font-family: Equinor;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
`

const EdsTableStyled = styled(EdsTable)`
    width: 100%;
`

const SignalStatusValue = styled(Typography)`
    margin-left: 5px;
    font-family: Equinor;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
`

const StatusContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const SensorStatus = styled(Typography)`
    margin-left: 5px;
    font-family: Equinor;
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
`

export default Table
